import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/authStore'
import type { ApiResponse } from '@/types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

const ISO_DATETIME_WITHOUT_TZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/

function normalizeTimestamps(data: unknown): unknown {
  if (typeof data === 'string' && ISO_DATETIME_WITHOUT_TZ.test(data)) {
    return data + 'Z'
  }
  if (Array.isArray(data)) {
    return data.map(normalizeTimestamps)
  }
  if (data && typeof data === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      result[key] = normalizeTimestamps(value)
    }
    return result
  }
  return data
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => {
    if (response.data) {
      response.data = normalizeTimestamps(response.data)
    }
    return response
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const status = error.response?.status

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = useAuthStore.getState().refreshToken
      if (refreshToken) {
        try {
          const response = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
            `${API_BASE_URL}/play/auth/refresh`,
            { refreshToken }
          )

          if (response.data.success && response.data.data) {
            const { accessToken, refreshToken: newRefreshToken } = response.data.data
            useAuthStore.getState().setTokens(accessToken, newRefreshToken)

            originalRequest.headers.Authorization = `Bearer ${accessToken}`
            return apiClient(originalRequest)
          }
        } catch {
          useAuthStore.getState().logout()
          return Promise.reject(error)
        }
      }
    }

    return Promise.reject(error)
  }
)

export const getApiErrorCode = (error: unknown): string | undefined => {
  if (axios.isAxiosError(error)) {
    return (error.response?.data as ApiResponse<unknown>)?.error?.code
  }
  return undefined
}

export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiResponse<unknown>
    return apiError?.error?.message || error.message || 'An error occurred'
  }
  return 'An unexpected error occurred'
}
